import { FileText, Home, Image, type LucideIcon, Music, Package, Palette, Type, Video } from 'lucide-react';

import { ICON_SIZE } from '@/components/ui/iconSize';
import { type BrandColour, type Material, sameShade } from './types';

/** The sections of one brand, in the order the sidebar lists them. Each is a path segment. */
export const BRAND_TABS = [
  'overview',
  'logo',
  'colour',
  'fonts',
  'visual-elements',
  'audio',
  'files',
  'products',
] as const;
export type BrandTab = (typeof BRAND_TABS)[number];

export const BRAND_TAB_ICONS: Record<BrandTab, LucideIcon> = {
  overview: Home,
  logo: Image,
  colour: Palette,
  fonts: Type,
  'visual-elements': Video,
  audio: Music,
  files: FileText,
  products: Package,
};

/** The tab that completes one mandatory material. */
export const TAB_OF_MATERIAL: Record<Material, BrandTab> = {
  logos: 'logo',
  colours: 'colour',
  font: 'fonts',
  guidelines: 'files',
};

/** Returns true when the value names a brand tab. */
export function isBrandTab(value: string | undefined): value is BrandTab {
  return BRAND_TABS.some((tab) => tab === value);
}

/** The slot, image and icon size of each brand logo size. */
export const BRAND_LOGO_SIZES = {
  md: { slot: 'size-11', image: 'size-9', icon: ICON_SIZE.lg },
  lg: { slot: 'size-12', image: 'size-10', icon: ICON_SIZE.xl },
} as const;

/** The colour a new palette row starts with. */
export const NEW_COLOUR: BrandColour = { label: '', cyan: 0, magenta: 0, yellow: 0, key: 0 };

/** Returns one proposal per shade, without the shades the palette already has. */
export function distinctShades<T extends BrandColour>(proposals: T[], covered: BrandColour[]): T[] {
  const kept: T[] = [];
  for (const proposal of proposals) {
    const seen = [...covered, ...kept].some((colour) => sameShade(colour, proposal));
    if (!seen) kept.push(proposal);
  }
  return kept;
}

/** Returns a typed channel value as a whole percentage from 0 to 100. */
export function clampPercent(value: string): number {
  return Math.min(100, Math.max(0, Math.round(Number(value) || 0)));
}
